import { createSlice } from "@reduxjs/toolkit";
import apiService from "@utils/apiService";
import { Constants } from "@constants/Constants";
import { updateUseCases, updateUseCaseDetail, setTotalSavedElements } from "./usecase.slice";

const initialState = {
  useCaseIsSaved: false,
  useCaseSaveSuccess: false,
  useCaseRemoveSuccess: false,
  useCaseSaveOrRemoveFailure: false,
  useCaseSaveOrRemoveFailureCode: '',
  useCaseSaveOrRemoveFailureMessage: '',
  useCaseSaving: false,
  useCaseRemoving: false,
  lastSavedUseCase: null,
  lastUnsavedUseCase: null,
};

export const saveToScorecardSlice = createSlice({
  name: "saveToScorecard",
  initialState,
  reducers: {
    setUseCaseIsSaved: (state, action) => {
      state.useCaseIsSaved = action.payload;
    },
    setUseCaseSaveSuccess: (state, action) => {
      state.useCaseSaveSuccess = action.payload;
    },
    resetUseCaseSaveSuccess: (state) => {
      state.useCaseSaveSuccess = false;
    },
    setUseCaseRemoveSuccess: (state, action) => {
      state.useCaseRemoveSuccess = action.payload;
    },
    resetUseCaseRemoveSuccess: (state) => {
      state.useCaseRemoveSuccess = false;
    },
    setUseCaseSaveOrRemoveFailure: (state, action) => {
      state.useCaseSaveOrRemoveFailure = action.payload?.value;
      state.useCaseSaveOrRemoveFailureCode = action.payload?.statusCode || '';
      state.useCaseSaveOrRemoveFailureMessage = action.payload?.message || '';
    },
    resetUseCaseSaveOrRemoveFailure: (state) => {
      state.useCaseSaveOrRemoveFailure = false;
      state.useCaseSaveOrRemoveFailureCode = '';
      state.useCaseSaveOrRemoveFailureMessage = '';
    },
    setLastSavedUseCase: (state, action) => {
      state.lastSavedUseCase = action.payload;
    },
    setLastUnsavedUseCase: (state, action) => {
      state.lastUnsavedUseCase = action.payload;
    },
    setUseCaseSaving: (state, action) => {
      state.useCaseSaving = action.payload;
    },
    setUseCaseRemoving: (state, action) => {
      state.useCaseRemoving = action.payload;
    },
  },
});
const { actions, reducer } = saveToScorecardSlice;
export const {
  setUseCaseIsSaved,
  setUseCaseSaveSuccess,
  resetUseCaseSaveSuccess,
  setUseCaseRemoveSuccess,
  resetUseCaseRemoveSuccess,
  setUseCaseSaveOrRemoveFailure,
  resetUseCaseSaveOrRemoveFailure,
  setLastSavedUseCase,
  setLastUnsavedUseCase,
  setUseCaseSaving,
  setUseCaseRemoving,
} = actions;

export const saveUseCaseToScorecard = (usecase, scorecardId, scoreCardName, popperScorecards, fromUndoAction) => (dispatch, getState) => {
  const useCaseId = usecase.id;
  dispatch(resetUseCaseRemoveSuccess());
  dispatch(setUseCaseSaveSuccess(false));
  dispatch(setUseCaseSaving(true));
  dispatch(setUseCaseSaveOrRemoveFailure({ value: false }));
  apiService.post(
    `${Constants.API.USE_CASE_DETAIL}/${useCaseId}${Constants.API.SCORE_CARD_TERM}${scorecardId}${Constants.API.SAVE}`
  ).then((response) => {
    dispatch(setUseCaseIsSaved(response?.data?.data?.isSaved));
    dispatch(setUseCaseSaveSuccess(true));
    dispatch(setLastSavedUseCase({
      usecase: usecase,
      scorecardId: scorecardId,
      scoreCardName: scoreCardName,
      popperScorecards: popperScorecards
    }));
    const state = getState();
    const currTotalSavedElements = state.usecases.totalSavedElements;
    const scorecardSavedLength = popperScorecards.filter(scorecard => scorecard.addedToScoreCard).length;
    if (!scorecardSavedLength || (fromUndoAction && scorecardSavedLength === 1)) {
      dispatch(setTotalSavedElements(currTotalSavedElements + 1));
      const currentUseCases = state.usecases.useCases.map(useCase => {
        if (useCase.id === useCaseId) {
          return {
            ...useCase,
            isSaved: true,
          }
        }
        return useCase;
      });
      dispatch(updateUseCases(currentUseCases))
      dispatch(updateUseCaseDetail({
        ...state.usecases.usecase,
        isSaved: true,
      }))
    }
  }).catch((error) => {
    if (error?.response?.status === 409) {
      dispatch(setUseCaseSaveOrRemoveFailure({
        value: true,
        statusCode: error?.response?.status,
        message: Constants.SCORECARD.SAVE_REMOVE_SCORECARD_CONFLICT_MESSAGE,
      }));
    } else if (error?.response?.status === 400) {
      dispatch(setUseCaseSaveOrRemoveFailure({
        value: true,
        statusCode: error?.response?.status,
        message: error?.response?.data?.systemMessage?.message,
      }));
    } else {
      dispatch(setUseCaseSaveOrRemoveFailure({ value: true }));
    }
  }).finally(() => {
    dispatch(setUseCaseSaving(false));
  })
}

export const removeUseCaseFromScorecard = (usecase, scorecardId, scoreCardName, popperScorecards) => (dispatch, getState) => {
  const useCaseId = usecase.id;
  dispatch(resetUseCaseSaveSuccess());
  dispatch(setUseCaseRemoving(true));
  dispatch(setUseCaseRemoveSuccess(false));
  dispatch(setUseCaseSaveOrRemoveFailure({ value: false }));
  return apiService.delete(
    `${Constants.API.USE_CASE_DETAIL}/${useCaseId}${Constants.API.SCORE_CARD_TERM}${scorecardId}${Constants.API.SAVE}`
  ).then((response) => {
    dispatch(setLastUnsavedUseCase({
      usecase: usecase,
      scorecardId: scorecardId,
      scoreCardName: scoreCardName,
      popperScorecards: popperScorecards
    }));
    dispatch(setUseCaseRemoveSuccess(true));
    dispatch(setUseCaseIsSaved(response?.data?.data?.isSaved));
    const state = getState();
    const currTotalSavedElements = state.usecases.totalSavedElements;
    const scorecardSavedLength = popperScorecards.filter(scorecard => scorecard.addedToScoreCard).length;
    if (scorecardSavedLength === 1) {
      dispatch(setTotalSavedElements(currTotalSavedElements - 1));
      const currentUseCases = state.usecases.useCases.map(useCase => {
        if (useCase.id === useCaseId) {
          return {
            ...useCase,
            isSaved: false,
          }
        }
        return useCase;
      });
      dispatch(updateUseCases(currentUseCases));
      dispatch(updateUseCaseDetail({
        ...state.usecases.usecase,
        isSaved: false,
      }))
    } 
  }).catch(() => {
    dispatch(setUseCaseSaveOrRemoveFailure({ value: true }));
  }).finally(() => {
    dispatch(setUseCaseRemoving(false));
  })
}

export default reducer;
